import { useEffect } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FeatureInfographic from "@/components/FeatureInfographic";
import SectionInfographic from "@/components/SectionInfographic";
import { TalkToAiEmployeeButton } from "@/components/TalkToAiEmployeeLauncher";

/* Reusable feature marketing page.
 * Each feature landing page passes:
 *   slug     — must match /api/infographics/{slug}
 *   accent   — hex, matches infographic + hero glow
 *   badge    — small "Now available" or similar label
 *   title    — H1
 *   tagline  — italic beneath H1
 *   subtitle — paragraph
 *   primaryCta {label, to}    — brand CTA
 *   secondaryCta {label, to}  — outline CTA
 *   flow     — [{n,title,desc}]  step-by-step
 *   capabilities — [{icon (Lucide name), title, desc}]
 *   safetyBullets — [[title, desc], ...]   optional
 *   industries — [{icon (Lucide), label, desc}]  optional
 *   closingHeadline / closingSubtitle
 */
export default function FeaturePageTemplate({
  slug, accent = "#4F46E5", badge, title, tagline, subtitle,
  primaryCta, secondaryCta, flow = [], capabilities = [],
  safetyBullets = [], industries = [], closingHeadline, closingSubtitle,
  brandStripe,
  testIdPrefix = "feature-page",
}) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} | WebsiteTech.pro`;
  }, [title]);

  const Icon = (name) => {
    const C = LucideIcons[name] || LucideIcons.Sparkles;
    return C;
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1" data-testid={testIdPrefix}>
        {/* Hero */}
        <section
          className="relative text-white pt-28 pb-20 overflow-hidden"
          style={{
            background: `linear-gradient(180deg, ${accent}22 0%, #0B1220 40%, #0B1220 100%)`,
          }}
        >
          <div
            className="absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full opacity-30 blur-3xl pointer-events-none"
            style={{ background: accent }}
          />
          <div className="relative max-w-6xl mx-auto px-6">
            {badge && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border"
                style={{ background: `${accent}22`, borderColor: `${accent}66`, color: "#fff" }}
                data-testid={`${testIdPrefix}-badge`}
              >
                <Sparkles className="w-3.5 h-3.5" /> {badge}
              </span>
            )}
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
              {title}
            </h1>
            {tagline && (
              <p className="mt-3 text-lg italic" style={{ color: accent }}>{tagline}</p>
            )}
            {subtitle && <p className="mt-6 text-lg text-slate-300 max-w-2xl">{subtitle}</p>}
            <div className="mt-8 flex flex-wrap gap-3">
              {primaryCta && (
                <Link
                  to={primaryCta.to}
                  data-testid={`${testIdPrefix}-primary-cta`}
                  className="inline-flex items-center gap-2 rounded-lg px-5 py-3 font-semibold text-white transition"
                  style={{ background: accent }}
                >
                  {primaryCta.label} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              <TalkToAiEmployeeButton size="md" variant="dark" />
              {secondaryCta && (
                <Link
                  to={secondaryCta.to}
                  data-testid={`${testIdPrefix}-secondary-cta`}
                  className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-3 font-semibold transition"
                >
                  {secondaryCta.label}
                </Link>
              )}
            </div>
            {brandStripe && (
              <p className="mt-6 text-xs text-slate-400 max-w-2xl">{brandStripe}</p>
            )}
          </div>
        </section>

        {/* Flow */}
        {flow.length > 0 && (
          <section className="py-16 bg-white">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-base md:text-lg font-bold uppercase tracking-widest" style={{ color: accent }}>
                How it works
              </h2>
              <p className="mt-2 text-3xl font-bold text-slate-900 max-w-3xl">
                A guided path from first touch to a measurable outcome.
              </p>
              <div className={`mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${Math.min(flow.length, 5)} gap-4`}>
                {flow.map((s) => (
                  <div key={s.n} className="relative rounded-xl border border-slate-200 p-5 bg-slate-50" data-testid={`${testIdPrefix}-flow-${s.n}`}>
                    <div
                      className="absolute -top-3 -left-3 w-8 h-8 rounded-full text-white grid place-items-center font-bold text-sm"
                      style={{ background: accent }}
                    >{s.n}</div>
                    <div className="text-sm font-semibold text-slate-900 mt-1">{s.title}</div>
                    <div className="mt-2 text-xs text-slate-600 leading-relaxed">{s.desc}</div>
                  </div>
                ))}
              </div>

              {/* AI-generated section infographic — how-it-works view */}
              <div className="mt-14">
                <SectionInfographic
                  slug={slug}
                  section="how-it-works"
                  theme="dark"
                  accent={accent}
                  caption="AI-generated · workflow view of this module"
                />
              </div>
            </div>
          </section>
        )}

        {/* Capabilities */}
        {capabilities.length > 0 && (
          <section className="py-20 bg-slate-50">
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900">Built for real business work</h2>
                  <p className="mt-2 text-slate-600 max-w-2xl">
                    Every capability is grounded in your approved knowledge, permission-gated, and auditable.
                  </p>
                </div>
                <div>
                  <SectionInfographic
                    slug={slug}
                    section="overview"
                    theme="dark"
                    accent={accent}
                    caption="AI-generated · overview illustration"
                  />
                </div>
              </div>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {capabilities.map((c) => {
                  const IconC = Icon(c.icon);
                  return (
                    <div
                      key={c.title}
                      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition"
                      data-testid={`${testIdPrefix}-cap-${c.title.split(' ')[0].toLowerCase()}`}
                    >
                      <IconC className="w-6 h-6" style={{ color: accent }} />
                      <div className="mt-4 font-semibold text-slate-900">{c.title}</div>
                      <div className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Outcomes — new dedicated section with stat-card infographic */}
        <section className="py-20 bg-white" data-testid={`${testIdPrefix}-outcomes`}>
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="lg:order-2">
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border"
                style={{ background: `${accent}15`, borderColor: `${accent}55`, color: accent }}
              >
                <LucideIcons.BarChart3 className="w-3.5 h-3.5" /> Outcomes
              </span>
              <h2 className="mt-4 text-3xl font-bold text-slate-900">
                The numbers change on day one.
              </h2>
              <p className="mt-4 text-slate-700">
                Teams typically see minutes-not-days turnarounds, dramatically wider
                coverage per week, and clean attribution back to the assets that
                actually converted. The card infographic on the left is an editorial
                visualisation of the outcomes this module unlocks.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-slate-800">
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-full grid place-items-center text-white text-[11px] font-bold" style={{ background: accent }}>1</span>
                  Minutes, not days — for a complete asset kit
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-full grid place-items-center text-white text-[11px] font-bold" style={{ background: accent }}>2</span>
                  10× coverage — brief more with the same team
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-full grid place-items-center text-white text-[11px] font-bold" style={{ background: accent }}>3</span>
                  Clean attribution — every asset traces to a conversion
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-full grid place-items-center text-white text-[11px] font-bold" style={{ background: accent }}>4</span>
                  Consistent brand — every asset uses the same DNA snapshot
                </li>
              </ul>
            </div>
            <div className="lg:order-1">
              <SectionInfographic
                slug={slug}
                section="outcomes"
                theme="dark"
                accent={accent}
                caption="AI-generated · outcomes card view"
              />
            </div>
          </div>
        </section>

        {/* Safety / control */}
        {safetyBullets.length > 0 && (
          <section className="py-20 bg-slate-900 text-white">
            <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
              <div>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border"
                  style={{ background: "#065F4633", borderColor: "#10B98166", color: "#D1FAE5" }}
                >
                  <LucideIcons.ShieldCheck className="w-3.5 h-3.5" /> Consent-first by design
                </span>
                <h2 className="mt-4 text-3xl font-bold">Safety, identity & control — not an afterthought</h2>
                <p className="mt-4 text-slate-300">
                  Every setting is client-controlled. Every action is auditable. PII gets redacted from stored transcripts by default.
                </p>
                <div className="mt-6">
                  <SectionInfographic
                    slug={slug}
                    section="guardrails"
                    theme="dark"
                    accent={accent}
                    caption="AI-generated · trust & guardrails"
                  />
                </div>
              </div>
              <div className="grid gap-3">
                {safetyBullets.map(([t, d]) => (
                  <div
                    key={t}
                    className="flex gap-3 items-start rounded-xl border border-white/10 bg-white/5 p-4"
                    data-testid={`${testIdPrefix}-safety-${t.split(' ')[0].toLowerCase()}`}
                  >
                    <LucideIcons.Lock className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-semibold">{t}</div>
                      <div className="mt-1 text-xs text-slate-400">{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Industries */}
        {industries.length > 0 && (
          <section className="py-20 bg-white">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-3xl font-bold text-slate-900">Prebuilt for your industry</h2>
              <p className="mt-2 text-slate-600 max-w-2xl">
                Templates ship with the right tone, refused topics, actions, and disclosures — editable at any time.
              </p>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {industries.map((it) => {
                  const IconC = Icon(it.icon);
                  return (
                    <div
                      key={it.label}
                      className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4"
                      data-testid={`${testIdPrefix}-ind-${it.label.split(' ')[0].toLowerCase()}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-white grid place-items-center border border-slate-200 flex-shrink-0">
                        <IconC className="w-4 h-4" style={{ color: accent }} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{it.label}</div>
                        <div className="mt-1 text-xs text-slate-600">{it.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Closing CTA */}
        <section
          className="py-20 text-white"
          style={{ background: `linear-gradient(180deg, ${accent}22 0%, #0B1220 100%)` }}
        >
          <div className="max-w-4xl mx-auto px-6 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: accent }} />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold">
              {closingHeadline || "Bring this to your business."}
            </h2>
            {closingSubtitle && (
              <p className="mt-4 text-slate-300 max-w-2xl mx-auto">{closingSubtitle}</p>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {primaryCta && (
                <Link
                  to={primaryCta.to}
                  data-testid={`${testIdPrefix}-final-primary`}
                  className="inline-flex items-center gap-2 rounded-lg px-6 py-4 font-semibold text-lg text-white transition"
                  style={{ background: accent }}
                >
                  {primaryCta.label} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              <TalkToAiEmployeeButton size="lg" variant="ghost" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
